import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { uploadLogo } from '../../services/SettingsService';
import { getLogo, deleteLocalLogo } from '../../services/LogoStorageService';
import { getIcon } from '../../utils/IconHandler';

const LogoUpload = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadLogo();
  }, []);

  // Cleanup: liberar URLs de blob cuando el componente se desmonte o cambie el logo
  useEffect(() => {
    return () => {
      if (logoUrl && typeof logoUrl === 'string' && logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [logoUrl]);

  const loadLogo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const empresaId = parseInt(localStorage.getItem('empresaId'), 10);
      if (!empresaId) {
        setLogoUrl(null);
        setLoading(false);
        return;
      }
      
      // Obtener logo desde almacenamiento local o descargarlo si no existe
      const logoUrl = await getLogo(empresaId);
      setLogoUrl(logoUrl);
    } catch (err) {
      console.error('Error loading logo:', err);
      // Si es 404, no hay logo, eso está bien
      if (err.response?.status !== 404) {
        setError(t('error_loading_logo') || 'Error al cargar el logo');
      }
      setLogoUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError(t('invalid_image_file') || 'Por favor selecciona un archivo de imagen');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('file_too_large') || 'El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(false);
      
      const empresaId = parseInt(localStorage.getItem('empresaId'), 10);
      if (!empresaId) {
        setError(t('error_uploading_logo') || 'Error: No se encontró el ID de la empresa');
        setUploading(false);
        return;
      }
      
      // Borrar el logo local antes de subir uno nuevo
      await deleteLocalLogo(empresaId);
      
      await uploadLogo(file);
      
      setSuccess(true);
      // Recargar el logo después de subirlo (se descargará y guardará automáticamente)
      await loadLogo();
      
      // Limpiar el input
      if (event.target) {
        event.target.value = '';
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.message || t('error_uploading_logo') || 'Error al subir el logo');
    } finally {
      setUploading(false);
    }
  };

  const handleImageError = () => {
    // Si falla la carga de la imagen, intentar recargar desde el servicio
    loadLogo();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 3, pt: 1 }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              {logoUrl ? (
                <Box
                  sx={{
                    width: { xs: '100%', md: 260 },
                    height: 130,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white'
                  }}
                >
                  <img
                    src={logoUrl}
                    alt="Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onError={handleImageError}
                    crossOrigin={logoUrl.startsWith('http') ? 'anonymous' : undefined}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: { xs: '100%', md: 260 },
                    height: 130,
                    bgcolor: 'action.hover',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    {t('no_logo') || 'Sin logo'}
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={500}>{t('company_logo') || 'Logo de la Empresa'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>Se muestra en documentos, comunicaciones y otros elementos de identidad.</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>{t('logo_uploaded_successfully') || 'Logo subido correctamente'}</Alert>}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {!readOnly && <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload-input"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />}
                {!readOnly && <label htmlFor="logo-upload-input">
                  <Button
                    variant="contained"
                    component="span"
                    disabled={uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : getIcon('cloudUpload')}
                  >
                    {uploading
                      ? (t('uploading') || 'Subiendo...')
                      : (t('upload_logo') || 'Subir Logo')}
                  </Button>
                </label>}
                {logoUrl && (
                  <Button
                    variant="outlined"
                    onClick={loadLogo}
                    disabled={uploading}
                    startIcon={getIcon('refresh')}
                  >
                    {t('refresh') || 'Actualizar'}
                  </Button>
                )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>{t('logo_upload_hint') || 'Formatos soportados: PNG, JPG. Tamaño máximo: 5MB'}</Typography>
              </Box>
            </>
          )}
    </Box>
  );
};

export default LogoUpload;
